import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import moment from 'moment';

import { FlexView, Row, Separator, Text } from '@cuemath/comps';
import { Button } from '@cuemath/web-comps';
import BallLoader from 'comps/ball-loader';
import COLORS from 'constants/colors';
import { closeModal } from 'actions/modal';

const Wrapper = styled(FlexView)`
  padding: 32px 24px 24px;
`;

const Detail = styled(FlexView)`
  background-color: ${COLORS.WILD_SAND};
  border-radius: 8px;
  padding: 16px;
`;

const DetailRow = styled(Row)`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const DateInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${COLORS.GALLERY};
  border-radius: 4px;
  font-size: 14px;
  color: ${COLORS.ABBEY};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const Warning = styled(FlexView)`
  background-color: ${COLORS.WARNING_BG};
  border-radius: 8px;
  padding: 12px 16px;
`;

const ButtonRow = styled(Row)`
  gap: 12px;
`;

const fmt = d => (d ? moment(d).format('DD MMM YYYY') : 'open');

const RegionPayoutEditEnd = ({
  region,
  ratio,
  count,
  latestStart,
  onSubmit,
  actions,
}) => {
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const today = moment().format('YYYY-MM-DD');

  const handleSubmit = async () => {
    if (!endDate) {
      setError('Pick a valid-until date.');
      return;
    }
    if (moment(endDate).isBefore(moment(today), 'day')) {
      setError('End date must be today or a future date.');
      return;
    }
    // New end must land strictly after the latest window's valid-from (day-granular,
    // since validity_start may carry a time component from the API).
    if (latestStart && !moment(endDate).isAfter(moment(latestStart), 'day')) {
      setError('Valid-until must be after valid-from.');
      return;
    }
    setError('');
    setLoading(true);
    const res = await onSubmit(endDate);
    // On success the parent closes this modal; only restore on failure.
    if (!res || !res.ok) {
      setError((res && res.error) || 'Failed to update windows.');
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Text variant="h2" align="center">
        Edit validity end
      </Text>
      <Separator height={8} transparent />
      <Text variant="body2" align="center" color={COLORS.GRAY}>
        {region} · {ratio}
      </Text>
      <Separator height={20} transparent />

      <Detail>
        <DetailRow justifyContent="space-between" alignItems="center">
          <Text variant="body2" color={COLORS.GRAY}>
            Windows to update
          </Text>
          <Text variant="body1Medium">{count}</Text>
        </DetailRow>
        <DetailRow justifyContent="space-between" alignItems="center">
          <Text variant="body2" color={COLORS.GRAY}>
            Latest valid from
          </Text>
          <Text variant="body1Medium">{fmt(latestStart)}</Text>
        </DetailRow>
      </Detail>

      <Separator height={16} transparent />

      <Text variant="body2" color={COLORS.GRAY}>
        New valid until
      </Text>
      <Separator height={6} transparent />
      <DateInput
        type="date"
        min={today}
        value={endDate}
        disabled={loading}
        onChange={e => {
          setEndDate(e.target.value);
          setError('');
        }}
      />

      {error ? (
        <>
          <Separator height={8} transparent />
          <Text variant="body2" color={COLORS.RED}>
            {error}
          </Text>
        </>
      ) : null}

      <Separator height={16} transparent />

      <Warning>
        <Text variant="body2" color={COLORS.ABBEY}>
          Sets the same valid-until on every active window shown for this region and
          ratio. Only future rate resolution is affected — fees already snapshotted keep
          their value. This is applied all-or-nothing: if any window would overlap a
          neighbour, none are changed and the error is shown here.
        </Text>
      </Warning>

      <Separator height={24} transparent />

      <ButtonRow justifyContent="center">
        {loading ? (
          <BallLoader />
        ) : (
          <>
            <Button
              variant="secondary"
              size="small"
              label="Cancel"
              width="160px"
              onClick={() => actions.closeModal()}
            />
            <Button size="small" label="Save" width="160px" onClick={handleSubmit} />
          </>
        )}
      </ButtonRow>
    </Wrapper>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ closeModal }, dispatch),
});

export default connect(null, mapDispatchToProps)(RegionPayoutEditEnd);
